import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { detailsVacApp, updateVacApp } from "../../actions/vaccineAppointmentActions";
import { useParams, useNavigate } from "react-router-dom";

const fieldNames = [
  "Somatic Concern",
  "Anxiety",
  "Emotional Withdrawal",
  "Conceptual Disorganization",
  "Guilt Feelings",
  "Tension",
  "Mannerisms and Posturing",
  "Grandiosity",
  "Depressive Mood",
  "Hostility",
  "Suspiciousness",
  "Hallucinatory Behavior",
  "Motor Retardation",
  "Uncooperativeness",
  "Unusual Thought Content",
  "Blunted Affect",
  "Excitement",
  "Disorientation",
];

const PsychiatricRating = () => {
  const [form2Data, setForm2Data] = useState({
    fields: Array(fieldNames.length).fill(null),
  });

  const [existingData, setExistingData] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const vaccineAppList = useSelector((state) => state.vaccineAppDetails);
  const {
    loading,
    error,
    appointment: { appointment, pastAppointments } = {},
  } = vaccineAppList || {};

  useEffect(() => {
    if (userInfo) {
      dispatch(detailsVacApp(id));
    } else {
      navigate("/signin");
    }
  }, [dispatch, userInfo, id, navigate]);

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const submittedData = fieldNames.reduce((result, fieldName, index) => {
      result[fieldName] = form2Data.fields[index];
      return result;
    }, {});

    const totalScore = Object.values(submittedData).reduce(
      (acc, value) => acc + (value || 0),
      0
    );

    // Save score to localStorage for Psychodiagnostic form
    const localStorageKey = `psycho_tools_${id}`;
    const existingScores = JSON.parse(localStorage.getItem(localStorageKey) || '{}');
    existingScores['Brief Psychiatric Rating Scale'] = totalScore;
    localStorage.setItem(localStorageKey, JSON.stringify(existingScores));

    dispatch(
      updateVacApp({
        _id: appointment._id,
        psychiatric: {
          info: submittedData,
          score: totalScore,
        },
      })
    );

    alert("Form is submitted"); // replace with showToast if you have it
  };

  const handleRadioChange = (index, value) => {
    setForm2Data((prevData) => {
      const updatedFields = [...prevData.fields];
      updatedFields[index] = value;
      return { ...prevData, fields: updatedFields };
    });
  };

  useEffect(() => {
    setExistingData(appointment?.psychiatric ? appointment?.psychiatric?.info : null);
  }, [appointment]);

  useEffect(() => {
    if (existingData) {
      setForm2Data({ fields: Object.values(existingData) });
    }
  }, [existingData]);

  return (
    <div style={{ padding: "30px", fontFamily: "Segoe UI, sans-serif", background: "#f5f7fa", minHeight: "100vh" }}>
      <div style={{ maxWidth: "900px", margin: "0 auto", background: "#fff", padding: "25px", borderRadius: "12px", boxShadow: "0 4px 15px rgba(0,0,0,0.08)" }}>
        <h2 style={{ marginBottom: "10px", color: "#2c3e50" }}>
          Brief Psychiatric Rating Scale (BPRS)
        </h2>
        <p style={{ marginBottom: "20px", color: "#555" }}>
          Please rate each item based on severity observed.
        </p>
        <div style={{ marginBottom: "20px", fontStyle: "italic", color: "#666", background: "#f0f4f8", padding: "10px", borderRadius: "8px" }}>
          <strong>Scale:</strong> 0 = Not Present, 1 = Very Mild, 2 = Mild, 3 = Moderate, 4 = Moderately Severe, 5 = Severe, 6 = Extremely Severe
        </div>

        <form onSubmit={handleFormSubmit}>
          {fieldNames.map((fieldName, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: "20px",
                padding: "15px",
                border: "1px solid #e1e5eb",
                borderRadius: "10px",
                background: "#fafafa",
                transition: "all 0.2s ease-in-out",
              }}
            >
              <label style={{ fontWeight: "600", color: "#34495e", display: "block", marginBottom: "8px" }}>
                {fieldName}
              </label>
              <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
                {[0, 1, 2, 3, 4, 5, 6].map((num) => (
                  <label
                    key={num}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      background: form2Data.fields[idx] === num ? "#3498db" : "#fff",
                      color: form2Data.fields[idx] === num ? "#fff" : "#333",
                      border: form2Data.fields[idx] === num ? "1px solid #2980b9" : "1px solid #ccc",
                      transition: "0.2s",
                    }}
                  >
                    <input
                      type="radio"
                      name={fieldName}
                      value={num}
                      checked={form2Data.fields[idx] === num}
                      onChange={() => handleRadioChange(idx, num)}
                      style={{ display: "none" }}
                    />
                    {num}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <button
            type="submit"
            style={{
              padding: "12px 24px",
              background: "#2ecc71",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              transition: "background 0.3s ease",
            }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#27ae60")}
            onMouseOut={(e) => (e.currentTarget.style.background = "#2ecc71")}
          >
            Save
          </button>
        </form>

        {appointment?.psychiatric?.totalScore !== undefined && (
          <div
            style={{
              marginTop: "25px",
              padding: "15px",
              background: "#ecf9f1",
              border: "1px solid #b2dfdb",
              borderRadius: "8px",
              fontWeight: "bold",
              color: "#2e7d32",
            }}
          >
            Current Score: {appointment.psychiatric.totalScore}
          </div>
        )}
      </div>
    </div>
  );
};

export default PsychiatricRating;
